import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { FileText, Search, Save, Users, ChevronRight, Loader2, ChevronDown } from 'lucide-react';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import { getUserAccountId, getUserRole } from '../lib/access';

// Department -> keyword mapping (mirrors DepartmentCockpit)
const DEPARTMENTS = [
  { id: 'all',        label: 'All Departments' },
  { id: 'finance',    label: 'Finance',        keywords: ['finance', 'financial', 'p&l', 'revenue', 'profit', 'cash'] },
  { id: 'sales',      label: 'Sales',          keywords: ['sales', 'pipeline', 'forecast', 'region', 'dealer', 'crm'] },
  { id: 'operations', label: 'Operations',     keywords: ['operations', 'operational', 'plant', 'throughput', 'uptime'] },
  { id: 'hr',         label: 'HR',             keywords: ['hr', 'human capital', 'people', 'headcount', 'attrition', 'payroll'] },
  { id: 'supply',     label: 'Supply Chain',   keywords: ['supply', 'supply chain', 'inventory', 'otif', 'procurement'] },
  { id: 'customer',   label: 'Customer',       keywords: ['customer', 'nps', 'csat', 'retention', 'cx'] },
];

function matchesDept(report, deptId) {
  if (deptId === 'all') return true;
  const dept = DEPARTMENTS.find(d => d.id === deptId);
  if (!dept) return true;
  const haystack = [report.title, report.name, report.parent, report.category, report.department]
    .filter(Boolean).join(' ').toLowerCase();
  return dept.keywords.some(k => haystack.includes(k));
}

function flattenReports(reports) {
  const out = [];
  const walk = (items, parentLabel) => {
    items?.forEach(r => {
      out.push({ ...r, _parent: parentLabel || null });
      if (r.children?.length) walk(r.children, r.title || r.name);
    });
  };
  walk(reports, null);
  return out;
}

function ReportRow({ report, depth, checked, onChange }) {
  const [open, setOpen] = useState(true);
  const hasKids = report.children?.length > 0;
  const label = report.title || report.name || 'Untitled';
  const isSecure = report.isSecure;
  const pad = (depth || 0);

  return (
    <>
      <div
        className="flex items-center gap-3 py-2.5 border-b border-border/40 hover:bg-muted/20 transition"
        style={{ paddingLeft: (16 + pad * 24) + 'px', paddingRight: '16px' }}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={val => onChange(report, val === true)}
        />
        {hasKids && (
          <button onClick={() => setOpen(o => !o)} className="text-muted-foreground hover:text-foreground">
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        )}
        <div className={(isSecure ? 'bg-amber-50' : 'bg-emerald-50') + ' h-7 w-7 rounded-md grid place-items-center flex-shrink-0'}>
          <FileText className={(isSecure ? 'text-amber-600' : 'text-emerald-600') + ' h-3.5 w-3.5'} />
        </div>
        <span className="text-sm flex-1 truncate">{label}</span>
        {isSecure && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">RLS</span>}
      </div>
      {hasKids && open && report.children.map(child => (
        <ReportRow
          key={child.id || child.reportId}
          report={child}
          depth={(depth || 0) + 1}
          checked={checked}
          onChange={onChange}
        />
      ))}
    </>
  );
}

export function ReportAllocationPage() {
  const account = useSelector(s => s.account);

  const [members,      setMembers]      = useState([]);
  const [allReports,   setAllReports]   = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userReports,  setUserReports]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [dirty,        setDirty]        = useState(false);
  const [dept,         setDept]         = useState('all');
  const [query,        setQuery]        = useState('');
  const [userKpis,     setUserKpis]     = useState([]);
  const [kpiLoading,   setKpiLoading]   = useState(false);
  const [kpiSaving,    setKpiSaving]    = useState(false);
  const [kpiDirty,     setKpiDirty]     = useState(false);
  const [allocationType, setAllocationType] = useState('reports');

  const userRole     = getUserRole(account || {});
  const isOwner      = userRole === 'Owner';
  const isSuperAdmin = isOwner || userRole === 'Super Admin';
  const allocatorUserId = getUserAccountId(account || {});

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetch = async () => {
      try {
        let res;
        if (isOwner) {
          res = await axiosInstance.get(configData.API_SERVER + 'organization/get_org_member_with_org_id?user=Owner');
        } else {
          const orgId = account?.org?.id || account?.org_data?.data?.id;
          res = await axiosInstance.get(configData.API_SERVER + 'organization/get_org_member_with_org_id?org_id=' + orgId + '&user=' + userRole);
        }
        setMembers(res?.data || []);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [isSuperAdmin, isOwner, userRole]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetch = async () => {
      try {
        let res;
        if (isOwner) {
          res = await axiosInstance.get(configData.API_SERVER + 'user/get_all_reports');
        } else {
          const orgId = account?.org?.id || account?.org_data?.data?.id;
          res = await axiosInstance.get(configData.API_SERVER + 'user/allocated-reports?org_id=' + orgId);
        }
        setAllReports(res?.data || []);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [account, isSuperAdmin]);

  useEffect(() => {
    if (!selectedUser) return;
    const fetch = async () => {
      setLoading(true);
      try {
        let res;
        res = await axiosInstance.get(configData.API_SERVER + 'user/get_allocated_reports_to_manage?user_id=' + selectedUser.id);
        setUserReports(flattenReports(res?.data || []));
      } catch (e) {
        console.error(e);
        setUserReports(flattenReports(allReports).map(r => ({ ...r, checked: false })));
      }
      setLoading(false);
      setDirty(false);
    };
    fetch();
  }, [selectedUser, isOwner]);

  useEffect(() => {
    if (!selectedUser) {
      setUserKpis([]);
      return;
    }
    const orgId = selectedUser.org_id || selectedUser.org?.id || account?.org?.id || account?.org_data?.data?.id;
    setKpiLoading(true);
    axiosInstance.get(`${String(configData.API_SERVER || '').replace(/\/+$/, '')}/activity/user-kpi-allocations/`, {
      params: {
        user_id: selectedUser.id,
        allocator_user_id: allocatorUserId,
        allocator_role: userRole,
        ...(orgId ? { org_id: orgId } : {}),
      },
    }).then(res => {
      setUserKpis(Array.isArray(res?.data) ? res.data : []);
      setKpiDirty(false);
    }).catch(error => {
      console.error('Unable to load KPI allocations', error);
      setUserKpis([]);
    }).finally(() => setKpiLoading(false));
  }, [selectedUser, account]);

  const saveKpis = async () => {
    if (!selectedUser) return;
    const orgId = selectedUser.org_id || selectedUser.org?.id || account?.org?.id || account?.org_data?.data?.id;
    setKpiSaving(true);
    try {
      await axiosInstance.post(
        `${String(configData.API_SERVER || '').replace(/\/+$/, '')}/activity/user-kpi-allocations/`,
        {
          user_id: selectedUser.id,
          allocator_user_id: allocatorUserId,
          allocator_role: userRole,
          ...(orgId ? { org_id: orgId } : {}),
          kpi_ids: userKpis.filter(kpi => kpi.checked).map(kpi => kpi.id),
        }
      );
      setKpiDirty(false);
    } catch (error) {
      console.error('Unable to save KPI allocations', error);
    } finally {
      setKpiSaving(false);
    }
  };

  const visibleReports = useMemo(() => {
    const q = query.toLowerCase();
    return allReports.filter(r => {
      const label = (r.title || r.name || '').toLowerCase();
      return (!q || label.includes(q)) && matchesDept(r, dept);
    });
  }, [allReports, dept, query]);

  const visibleKpis = useMemo(() => {
    const q = query.trim().toLowerCase();
    return userKpis.filter(kpi => {
      const department = String(kpi.department || '').toLowerCase();
      const selectedDepartment = DEPARTMENTS.find(item => item.id === dept);
      const departmentMatches = dept === 'all' || selectedDepartment?.keywords?.some(keyword => department.includes(keyword));
      const text = `${kpi.kpi_card_name || ''} ${kpi.kpi_code || ''}`.toLowerCase();
      return departmentMatches && (!q || text.includes(q));
    });
  }, [userKpis, dept, query]);

  const isChecked = (report) => {
    const id = report.id || report.reportId;
    const found = userReports.find(r => (r.id || r.reportId) === id);
    return found ? (found.checked === true) : false;
  };

  const handleToggle = (report, val) => {
    if (!selectedUser) return;
    const id = report.id || report.reportId;
    setUserReports(prev => {
      const existing = prev.find(r => (r.id || r.reportId) === id);
      if (existing) return prev.map(r => (r.id || r.reportId) === id ? { ...r, checked: val } : r);
      return [...prev, { ...report, checked: val }];
    });
    report.children?.forEach(child => handleToggle(child, val));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);

    try {
      const checkedIds = userReports
        .filter(r => r.checked === true && r.type !== 'collapse')
        .map(r => ({ reportId: r.id || r.reportId }));

      const payload = {
        userId: selectedUser.id,
        reportId: checkedIds,
      };

      console.log("REPORT ALLOCATION PAYLOAD", payload);

      const response = await axiosInstance.post(
        `${String(configData.API_SERVER || '').replace(/\/+$/, '')}/user/allocate-report`,
        payload
      );

      const allocatedCount = response?.data?.allocated_count;
      if (Number.isFinite(allocatedCount)) {
        setUserReports(prev => prev.map(report => ({ ...report, checked: report.checked === true })));
      }

      setDirty(false);
    } catch (e) {
      console.error(e);
    }

    setSaving(false);
  };
  
  if (!isSuperAdmin) {
    return (
      <div className="p-10">
        <div className="surface-card rounded-2xl p-12 text-center">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h2 className="font-display text-xl font-semibold">Super Admin access required</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign in as Super Admin or Owner to allocate reports.</p>
        </div>
      </div>
    );
  }

  const assignedCount = userReports.filter(r => r.checked === true).length;

  return (
    <div className="pb-12">
      <PageHeader
        eyebrow="Administration"
        title="User Dashboard Allocation"
        description="Assign Power BI reports and KPI cards to each user. Changes take effect immediately after saving."
        actions={
          <Button
            onClick={allocationType === 'reports' ? handleSave : saveKpis}
            disabled={allocationType === 'reports' ? (!dirty || saving || !selectedUser) : (!kpiDirty || kpiSaving || !selectedUser)}
          >
            {(allocationType === 'reports' ? saving : kpiSaving)
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            {allocationType === 'reports'
              ? (saving ? 'Saving…' : dirty ? 'Save report access' : 'Reports saved')
              : (kpiSaving ? 'Saving…' : kpiDirty ? 'Save KPI access' : 'KPIs saved')}
          </Button>
        }
      />

      <div className="px-6 lg:px-10 mb-4 flex gap-1 rounded-xl bg-muted/50 p-1 w-fit">
        <button onClick={() => { setAllocationType('reports'); setQuery(''); setDept('all'); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${allocationType === 'reports' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}>
          Power BI reports
        </button>
        <button onClick={() => { setAllocationType('kpis'); setQuery(''); setDept('all'); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${allocationType === 'kpis' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}>
          KPI cards
        </button>
      </div>

      <div className="px-6 lg:px-10 grid lg:grid-cols-[300px_1fr] gap-6">

        {/* User panel */}
        <div className="surface-card rounded-2xl p-4 flex flex-col gap-3 h-fit">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">Select user</p>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {members.map(m => {
              const name = m.memberName || m.name || m.org_owner || m.email || 'Unknown';
              const email = m.email || '';
              const isSelected = selectedUser?.id === m.id;
              const initials = name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
              return (
                <button
                  key={m.id}
                  onClick={() => setSelectedUser(m)}
                  className={'w-full flex items-center gap-3 p-2.5 rounded-xl border transition text-left ' + (isSelected ? 'border-primary bg-primary/8' : 'border-transparent hover:bg-muted/40')}
                >
                  <div className="h-9 w-9 rounded-full bg-[image:var(--gradient-primary)] grid place-items-center text-[11px] font-bold text-white flex-shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{email || m.role || ''}</p>
                  </div>
                  {isSelected && <ChevronRight className="h-4 w-4 text-primary flex-shrink-0" />}
                </button>
              );
            })}
            {members.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No users found</p>
            )}
          </div>
        </div>

        {allocationType === 'reports' ? (
        /* Report allocation panel */
        <div className="surface-card rounded-2xl overflow-hidden flex flex-col">

          {/* Toolbar */}
          <div className="flex items-center gap-3 p-4 border-b border-border/60 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedUser ? (
                <>
                  <div className="h-8 w-8 rounded-full bg-[image:var(--gradient-primary)] grid place-items-center text-[10px] font-bold text-white flex-shrink-0">
                    {(selectedUser.memberName || selectedUser.name || selectedUser.email || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-tight">{selectedUser.memberName || selectedUser.name || selectedUser.email}</p>
                    <p className="text-[11px] text-muted-foreground">{assignedCount} report{assignedCount !== 1 ? 's' : ''} assigned</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">← Select a user to manage their report access</p>
              )}
            </div>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-[200px]">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 h-8 text-sm" placeholder="Search reports…" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading reports…</span>
            </div>
          ) : !selectedUser ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Users className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">Select a user from the left panel</p>
            </div>
          ) : visibleReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <FileText className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">No reports found for this filter</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
              {DEPARTMENTS.filter(d => d.id !== 'all' && (dept === 'all' || dept === d.id)).map(d => {
                const dReports = visibleReports.filter(r => matchesDept(r, d.id));
                if (!dReports.length) return null;
                const allOn = dReports.every(r => isChecked(r));
                const someOn = dReports.some(r => isChecked(r));
                return (
                  <div key={d.id}>
                    <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 border-b border-border/60">
                      <Checkbox
                        checked={allOn ? true : someOn ? 'indeterminate' : false}
                        onCheckedChange={val => dReports.forEach(r => handleToggle(r, val === true))}
                      />
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.label}</span>
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {dReports.filter(r => isChecked(r)).length} / {dReports.length}
                      </Badge>
                    </div>
                    {dReports.map(r => (
                      <ReportRow key={r.id || r.reportId} report={r} depth={0} checked={isChecked(r)} onChange={handleToggle} />
                    ))}
                  </div>
                );
              })}

              {/* Other — reports that don't match any department keyword */}
              {(() => {
                const deptList = DEPARTMENTS.filter(d => d.id !== 'all');
                const otherReports = visibleReports.filter(r =>
                  !deptList.some(d => matchesDept(r, d.id))
                );
                if (!otherReports.length || (dept !== 'all')) return null;
                const allOn  = otherReports.every(r => isChecked(r));
                const someOn = otherReports.some(r => isChecked(r));
                return (
                  <div key="other">
                    <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 border-b border-border/60">
                      <Checkbox
                        checked={allOn ? true : someOn ? 'indeterminate' : false}
                        onCheckedChange={val => otherReports.forEach(r => handleToggle(r, val === true))}
                      />
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Other / General</span>
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {otherReports.filter(r => isChecked(r)).length} / {otherReports.length}
                      </Badge>
                    </div>
                    {otherReports.map(r => (
                      <ReportRow key={r.id || r.reportId} report={r} depth={0} checked={isChecked(r)} onChange={handleToggle} />
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        ) : (
        <div className="surface-card rounded-2xl overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 p-4 border-b border-border/60 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedUser ? (
                <>
                  <div className="h-8 w-8 rounded-full bg-[image:var(--gradient-primary)] grid place-items-center text-[10px] font-bold text-white flex-shrink-0">
                    {(selectedUser.memberName || selectedUser.email || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-tight">{selectedUser.memberName || selectedUser.email}</p>
                    <p className="text-[11px] text-muted-foreground">{userKpis.filter(kpi => kpi.checked).length} KPI card{userKpis.filter(kpi => kpi.checked).length !== 1 ? 's' : ''} assigned</p>
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">← Select a user to manage their KPI access</p>}
            </div>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative w-[200px]">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 h-8 text-sm" placeholder="Search KPI cards…" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
          </div>
          {kpiLoading ? (
            <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Loading KPI Master…</span></div>
          ) : !selectedUser ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground"><Users className="h-10 w-10 mb-3 opacity-30" /><p className="text-sm">Select a user from the left panel</p></div>
          ) : visibleKpis.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground"><FileText className="h-10 w-10 mb-3 opacity-30" /><p className="text-sm">No KPI Master cards found for this filter</p></div>
          ) : (
            <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
              {Object.entries(visibleKpis.reduce((groups, kpi) => {
                const key = kpi.department || 'Other / General';
                (groups[key] ||= []).push(kpi);
                return groups;
              }, {})).map(([department, kpis]) => (
                <div key={department}>
                  <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 border-b border-border/60">
                    <Checkbox checked={kpis.every(kpi => kpi.checked) ? true : kpis.some(kpi => kpi.checked) ? 'indeterminate' : false} onCheckedChange={value => {
                      const ids = new Set(kpis.map(kpi => kpi.id));
                      setUserKpis(prev => prev.map(kpi => ids.has(kpi.id) ? { ...kpi, checked: value === true } : kpi));
                      setKpiDirty(true);
                    }} />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{department}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">{kpis.filter(kpi => kpi.checked).length} / {kpis.length}</Badge>
                  </div>
                  {kpis.map(kpi => (
                    <label key={kpi.id} className="flex items-center gap-3 py-2.5 px-4 border-b border-border/40 hover:bg-muted/20 transition cursor-pointer">
                      <Checkbox checked={kpi.checked === true} onCheckedChange={value => {
                        setUserKpis(prev => prev.map(item => item.id === kpi.id ? { ...item, checked: value === true } : item));
                        setKpiDirty(true);
                      }} />
                      <div className="h-7 w-7 rounded-md bg-emerald-50 grid place-items-center text-emerald-700 text-[10px] font-bold">KPI</div>
                      <span className="text-sm flex-1">{kpi.kpi_card_name || kpi.kpi_code}</span>
                      <span className="text-[10px] text-muted-foreground">{kpi.kpi_code}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

      </div>
    </div>
  );
}
